'use client';

import { type ReactNode, useCallback, useRef } from 'react';
import { alpha } from '@mui/material/styles';
import { Box } from '@/primitives';
import { Pagination, type PaginationProps } from '@/primitives/Pagination';
import { black } from '@/lib/colours';
import { useDeferredReveal } from './useDeferredReveal';
import { findScrollParent, useGridScrollRestore } from './useGridScrollRestore';

/** A one-line compact lot row, for rows that have not rendered yet. */
const ROW_PLACEHOLDER_HEIGHT = 60;
/** Skeleton rows shown while the lots load. */
const SKELETON_ROWS = 8;

type PaginationModel = { page: number; pageSize: number };

function DeferredRow({
  cacheKey,
  children,
}: {
  cacheKey?: string;
  children: ReactNode;
}) {
  const { ref, visible, placeholderHeight } = useDeferredReveal(
    cacheKey,
    ROW_PLACEHOLDER_HEIGHT,
  );
  return (
    <Box ref={ref} sx={{ minHeight: visible ? undefined : placeholderHeight }}>
      {visible && children}
    </Box>
  );
}

type DeferredSaleLotListProps<Row> = {
  rows: readonly Row[];
  /** Stable per lot: keys the rows and their remembered heights. */
  getRowKey: (row: Row, index: number) => string;
  /** The compact row, as the grid's cell renders it. */
  renderRow: (row: Row) => ReactNode;
  loading: boolean;
  rowCount: number;
  paginationModel: PaginationModel;
  pageSizeOptions?: PaginationProps['pageSizeOptions'];
  onPaginationModelChange: (model: PaginationModel) => void;
  /** One skeleton row, repeated while the lots load. */
  skeleton: ReactNode;
  /** Shown when the search has no lots. */
  noRows: ReactNode;
  /** As for the grid: keeps and restores the page's scroll position. */
  scrollRestoreKey?: string;
};

/**
 * The phone and tablet lot list when ALL lots are shown. Every lot is listed
 * in the page's own scroll, like the cards. The grid would need a bounded
 * panel to stay virtualized, and that panel scrolled inside the page: a
 * second scrollbar. Rows mount as they come near the viewport, so a catalogue
 * of 800 lots stays cheap.
 */
export function DeferredSaleLotList<Row>({
  rows,
  getRowKey,
  renderRow,
  loading,
  rowCount,
  paginationModel,
  pageSizeOptions,
  onPaginationModelChange,
  skeleton,
  noRows,
  scrollRestoreKey,
}: DeferredSaleLotListProps<Row>) {
  const listRef = useRef<HTMLDivElement>(null);
  // Below md the public-search layout box scrolls the results, as for the
  // compact grid.
  const getScroller = useCallback(
    () => (listRef.current ? findScrollParent(listRef.current) : null),
    [],
  );

  useGridScrollRestore({
    scrollKey: scrollRestoreKey,
    getScroller,
    listRef,
    rowsReady: !loading && rowCount > 0 && rows.length > 0,
  });

  let content: ReactNode;
  if (loading) {
    content = Array.from({ length: SKELETON_ROWS }, (_, index) => (
      <Box key={index}>{skeleton}</Box>
    ));
  } else if (rows.length === 0) {
    content = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        {noRows}
      </Box>
    );
  } else {
    content = rows.map((row, index) => {
      const key = getRowKey(row, index);
      return (
        <DeferredRow
          key={key}
          cacheKey={scrollRestoreKey && `${scrollRestoreKey}|${key}`}
        >
          {renderRow(row)}
        </DeferredRow>
      );
    });
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        ref={listRef}
        sx={{
          // The compact grid's frame, so the list looks the same with ALL.
          bgcolor: 'background.paper',
          borderRadius: '8px',
          py: '4px',
          overflow: 'hidden',
          boxShadow: `0 2px 8px ${alpha(black[900], 0.08)}`,
        }}
      >
        {content}
      </Box>
      <Box sx={{ flexShrink: 0, display: 'flex', pt: { xs: 0.5, sm: 1.5 } }}>
        <Pagination
          page={paginationModel.page}
          pageCount={
            paginationModel.pageSize === -1
              ? 1
              : Math.ceil(rowCount / paginationModel.pageSize)
          }
          pageSize={paginationModel.pageSize}
          pageSizeOptions={pageSizeOptions}
          rowCount={rowCount}
          onPageChange={(page) =>
            onPaginationModelChange({ ...paginationModel, page })
          }
          onPageSizeChange={(pageSize) =>
            onPaginationModelChange({ page: 0, pageSize })
          }
        />
      </Box>
    </Box>
  );
}
